// Leave permission utilities for HR Portal
// Business logic for determining leave application actions (delete, cancel, edit)

import { extractDateInSingaporeTimezone, getTodayInSingaporeTimezone } from './dateUtils';

/**
 * Compare leave application PKs from mixed sources (JSON number vs URL string).
 * Notification deep-links pass `?approve=<id>` as a string; list payloads use
 * an integer AutoField — strict `===` misses the L2 Trade Leader inbox.
 */
export const sameLeaveApplicationId = (left, right) => {
  if (left == null || right == null || left === '' || right === '') {
    return false;
  }
  return String(left) === String(right);
};

/**
 * Safely resolve leave status from various field names
 * @param {Object} leave - Leave application object
 * @returns {string} Leave status or 'unknown' if missing
 */
export const resolveLeaveStatus = (leave) => {
  const status = leave?.leave_status || leave?.status;
  if (!status) {
    console.error('Leave application missing status:', leave?.id);
    return 'unknown';
  }
  return status;
};

/**
 * Check if leave is partially approved (multi-level approval in progress)
 * @param {Object} leave - Leave application object
 * @returns {boolean} True if partially approved
 */
export const isPartiallyApproved = (leave) => {
  if (!leave?.approval_workflow) {
    return false; // No workflow = not multi-level
  }
  
  const status = resolveLeaveStatus(leave);
  if (status !== 'submitted') {
    return false; // Only 'submitted' status can be partially approved
  }
  
  // Check if there are more levels to approve
  const currentLevel = leave.current_approval_level || 1;
  const approvalHistory = leave.approval_history || [];
  
  // If approval_history exists and current_level > 1, it's likely partial
  // We check if there's approval history but status is still 'submitted'
  if (currentLevel > 1 && approvalHistory.length > 0) {
    // If we have approvals but status is still 'submitted', it's partial
    return true;
  }
  
  // If current_level is 1 but we have approval_workflow, check if it's multi-level
  // For now, if workflow exists and status is 'submitted', assume it could be partial
  if (currentLevel === 1 && approvalHistory.length === 0 && status === 'submitted') {
    // This might be the first level, so not partial yet
    return false;
  }
  
  return false;
};

/**
 * Check if leave is fully approved
 * @param {Object} leave - Leave application object
 * @returns {boolean} True if fully approved
 */
export const isFullyApproved = (leave) => {
  const status = resolveLeaveStatus(leave);
  return status === 'approved' || status === 'auto_approved';
};

/**
 * Check whether an employee should see the Home/Urgent Leave extension entry
 * point. The eligibility endpoint remains authoritative for policy limits and
 * pending extensions; this helper only determines when the action is relevant.
 *
 * @param {Object} leave - Leave application object
 * @param {string|null} todayInSG - Today's date in YYYY-MM-DD format
 * @returns {boolean} True when the employee can open the extension flow
 */
export const canRequestLeaveExtension = (leave, todayInSG = null) => {
  if (!leave || leave.is_extension) return false;

  const leaveTypeName = (
    leave.leave_type?.leave_type_name
    || leave.leave_type_name
    || ''
  ).trim().toLowerCase();
  if (leaveTypeName !== 'home leave' && leaveTypeName !== 'urgent leave') {
    return false;
  }

  if (!isFullyApproved(leave)) return false;
  if (leave.flight_ticket_verification?.status !== 'approved') return false;

  const todayStr = todayInSG || getTodayInSingaporeTimezone();
  const startDate = extractDateInSingaporeTimezone(leave.leave_start_date);
  const endDate = extractDateInSingaporeTimezone(leave.leave_end_date);
  if (!todayStr || !startDate || !endDate) return false;

  return startDate <= todayStr && todayStr <= endDate;
};

/**
 * Check if leave date is in the past (Singapore timezone)
 * @param {Object} leave - Leave application object
 * @param {string} todayStr - Today's date in YYYY-MM-DD format (Singapore timezone)
 * @returns {boolean} True if leave date is in the past
 */
const isPastDate = (leave, todayStr) => {
  if (!leave?.leave_start_date) {
    return false; // Can't determine, default to safe (not past)
  }
  
  const leaveStartDateStr = extractDateInSingaporeTimezone(leave.leave_start_date);
  if (!leaveStartDateStr || !todayStr) {
    return false; // Safe default
  }
  
  return leaveStartDateStr < todayStr;
};

/**
 * Check if employee can HARD-DELETE a leave application.
 *
 * Hard delete is reserved for DRAFTS ONLY — never-submitted scratch records
 * that carry no balance impact, triggered no approval notification, and hold
 * no audit significance. The moment an application is submitted into the
 * approval workflow it becomes a record HR must be able to reconstruct, so it
 * is WITHDRAWN/CANCELLED (a soft transition to `cancelled` that preserves who
 * retracted it, when, and why) rather than erased. See `canCancelLeave`.
 *
 * @param {Object} leave - Leave application object
 * @returns {boolean} True if the (hard) delete button should be shown
 */
export const canDeleteLeave = (leave) => {
  if (!leave) return false;
  const status = resolveLeaveStatus(leave);
  return status === 'draft' || status === 'draft_saved';
};

/**
 * Check if employee can cancel leave application.
 *
 * The backend model property `can_be_cancelled` is the authoritative source — it
 * includes a same-day time-of-day check against the employee's scheduled start
 * time that the frontend cannot replicate without an extra API call.  When the
 * field is present in the API response we trust it directly.  The client-side
 * fallback (date-only) is kept for resilience in case the field is ever absent.
 *
 * @param {Object} leave - Leave application object
 * @param {string} todayInSG - Today's date in YYYY-MM-DD format (Singapore timezone), optional
 * @returns {boolean} True if cancel button should be shown
 */
export const canCancelLeave = (leave, todayInSG = null) => {
  if (!leave) return false;

  // Backend flag is authoritative (includes same-day start-time check)
  if (typeof leave.can_be_cancelled === 'boolean') {
    return leave.can_be_cancelled;
  }

  // --- Client-side fallback (date-only, no time-of-day precision) ---
  const status = resolveLeaveStatus(leave);
  
  // Verified sick/hospitalisation leaves are immutable once approved
  const lt = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
  const isVerifiedSick = ((lt.includes('outpatient') && lt.includes('sick')) || lt.includes('hospitalisation')) && status === 'approved';
  if (isVerifiedSick) return false;

  const todayStr = todayInSG || getTodayInSingaporeTimezone();
  
  if (isPartiallyApproved(leave)) {
    return !isPastDate(leave, todayStr);
  }
  
  if (isFullyApproved(leave)) {
    return !isPastDate(leave, todayStr);
  }
  
  if (status === 'submitted' || status === 'pending') {
    return !isPastDate(leave, todayStr);
  }
  
  return false;
};

/**
 * Resolve the user-facing verb for the soft-cancel action.
 *
 * IMPORTANT: This is a LABEL ONLY. Both outcomes hit the same `cancel`
 * endpoint and resolve to the single terminal status `cancelled` — no new
 * status is introduced. We distinguish the wording because retracting a
 * not-yet-approved request ("Withdraw") and revoking an already-granted
 * leave ("Cancel") are different mental models for the employee, even though
 * the system treats them identically.
 *
 * @param {Object} leave - Leave application object
 * @returns {'Withdraw'|'Cancel'} Verb to render on the action/modal
 */
export const getCancelActionLabel = (leave) => {
  const status = resolveLeaveStatus(leave);
  // Submitted / pending (including partially-approved, which stays 'submitted')
  // has NOT been granted yet → the employee is withdrawing their request.
  if (status === 'submitted' || status === 'pending') {
    return 'Withdraw';
  }
  // approved / auto_approved → revoking a granted leave.
  return 'Cancel';
};

